import { useSession, type Role } from '@/stores/session';

export interface Permission {
  /** The caller's single role in the ACTIVE school (null until bootstrapped). */
  role: Role | null;
  /** True when the active-membership role is one of `roles`. Deny-by-default:
   *  a null/unknown role never passes. */
  hasRole: (...roles: Role[]) => boolean;
  isAdmin: boolean;
  isTeacher: boolean;
  isPupil: boolean;
  isParent: boolean;
}

/**
 * Central permission read — ALL role gating in the UI goes through this hook
 * (or the `<Can>` component built on it). Never read `useSession().role`
 * directly in feature code, and remember: UI gating is UX only — the server
 * (Member + IsSchoolMember/HasSchoolRole) is the real authority.
 */
export function usePermission(): Permission {
  const role = useSession(s => s.role);
  return {
    role,
    hasRole: (...roles: Role[]) => role !== null && roles.includes(role),
    isAdmin: role === 'admin',
    isTeacher: role === 'teacher',
    isPupil: role === 'pupil',
    isParent: role === 'parent',
  };
}
